import { achievementReward, CATALOG, type SlotId } from "./catalog";
import type { RunRecord } from "../engine/engine";

/**
 * Local persistence — versioned save envelope (spec §20), localStorage-backed
 * in the web twin (the iOS app uses atomic file writes with a one-generation
 * backup; same schema).
 */

export interface Settings {
  music: boolean;
  fx: boolean;
  haptics: boolean;
  reduced: boolean;
  contrast: boolean;
  tether: boolean;
  nudge: boolean;
  offset: 44 | 24 | 0;
}

export interface TraceprintRec {
  id: string;
  /** rendered JPEG data URL (web twin); iOS stores PNG files */
  url: string;
  score: number;
  dateISO: string;
  fav: boolean;
  win: boolean;
  combo: number;
  durationTenths: number;
  runNumber: number;
  kind: string;
}

export interface Equipped {
  core: string;
  trail: string;
  palette: string;
  shapes: string;
  shard: string;
  arena: string;
  instrument: string;
  frame: string;
}

export interface SaveData {
  schemaVersion: number;
  tutorialVersion: number; // 0 = never completed
  bestClassic: number;
  bestDaily: Record<string, number>; // "2026-08-08" → score
  lastDailyPlayed: string | null;
  runs: number;
  totalRuns: number;
  cleared: boolean;
  achievements: string[];
  prism: boolean;
  equipped: Equipped;
  settings: Settings;
  prints: TraceprintRec[];
  printCounter: number;
}

const KEY = "echo_dodge_v1";
const SCHEMA = 1;
export const TUTORIAL_VERSION = 1;
export const MAX_PRINTS = 100;

export const DEFAULT_EQUIPPED: Equipped = {
  core: "pulse",
  trail: "standard",
  palette: "standard",
  shapes: "classic",
  shard: "star4",
  arena: "observatory",
  instrument: "puresine",
  frame: "plain",
};

export const DEFAULT_SETTINGS: Settings = {
  music: true,
  fx: true,
  haptics: true,
  reduced: false,
  contrast: false,
  tether: true,
  nudge: true,
  offset: 44,
};

function defaults(): SaveData {
  const reducedPref =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  return {
    schemaVersion: SCHEMA,
    tutorialVersion: 0,
    bestClassic: 0,
    bestDaily: {},
    lastDailyPlayed: null,
    runs: 0,
    totalRuns: 0,
    cleared: false,
    achievements: [],
    prism: false,
    equipped: { ...DEFAULT_EQUIPPED },
    settings: { ...DEFAULT_SETTINGS, reduced: reducedPref },
    prints: [],
    printCounter: 0,
  };
}

export class GameStore {
  data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    const base = defaults();
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return base;
      const envelope = JSON.parse(raw) as { schemaVersion: number; payload: Partial<SaveData> };
      const p = envelope.payload ?? {};
      return {
        ...base,
        ...p,
        equipped: { ...base.equipped, ...(p.equipped ?? {}) },
        settings: { ...base.settings, ...(p.settings ?? {}) },
        prints: Array.isArray(p.prints) ? p.prints : [],
        bestDaily: p.bestDaily ?? {},
      };
    } catch {
      return base;
    }
  }

  save(): void {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ schemaVersion: SCHEMA, writtenAt: new Date().toISOString(), payload: this.data }),
      );
    } catch {
      /* storage unavailable — gameplay continues (spec §20 resilience) */
    }
  }

  // ---- ownership ----

  owns(slot: SlotId, id: string): boolean {
    const item = CATALOG[slot].find((i) => i.id === id);
    if (!item) return false;
    if (item.free) return true;
    if (item.ach) return this.data.achievements.includes(item.ach);
    if (item.prism) return this.data.prism;
    return false;
  }

  equip(slot: SlotId, id: string): boolean {
    if (!this.owns(slot, id)) return false;
    this.data.equipped[slot] = id;
    this.save();
    return true;
  }

  /** Unknown equipped ids fall back visually without deleting ownership (spec §20.3). */
  equippedItem(slot: SlotId) {
    const id = this.data.equipped[slot];
    return CATALOG[slot].find((i) => i.id === id) ?? CATALOG[slot][0];
  }

  // ---- run results ----

  /**
   * Apply a finished non-practice run. Returns newly unlocked achievement ids
   * (idempotent; already-owned achievements never re-unlock) and best status.
   */
  applyRun(rec: RunRecord, edgeSteps: number, echoAliveSteps: number): {
    newAchievements: string[];
    isBest: boolean;
    prevBest: number;
  } {
    const d = this.data;
    d.totalRuns++;
    if (rec.kind === "classic" && !rec.wasTraining) d.runs++;

    let prevBest = d.bestClassic;
    let isBest = false;

    if (rec.wasTraining) {
      if (rec.outcome === "cleared") d.cleared = true;
      this.save();
      return { newAchievements: [], isBest: false, prevBest };
    }

    if (rec.kind === "daily") {
      const day = rec.dateISO.slice(0, 10);
      prevBest = d.bestDaily[day] ?? 0;
      isBest = rec.score > prevBest;
      if (isBest) d.bestDaily[day] = rec.score;
      d.lastDailyPlayed = day;
    } else {
      isBest = rec.score > d.bestClassic;
      if (isBest) d.bestClassic = rec.score;
    }
    if (rec.outcome === "cleared") d.cleared = true;

    const won = rec.outcome === "cleared";
    const checks: Record<string, boolean> = {
      first_light: rec.shardsCollected >= 1,
      first_echo: echoAliveSteps >= 600,
      timeline_clear: won,
      untouched: won && rec.remainingLives === 3,
      combo_four: rec.longestCombo >= 4,
      combo_eight: rec.longestCombo >= 8,
      score_1000: rec.score >= 1000,
      score_2000: rec.score >= 2000,
      boundary_dancer: won && edgeSteps >= 1200,
    };
    const fresh: string[] = [];
    for (const [id, hit] of Object.entries(checks)) {
      if (hit && !d.achievements.includes(id)) {
        d.achievements.push(id);
        fresh.push(id);
      }
    }
    this.save();
    return { newAchievements: fresh, isBest, prevBest };
  }

  /** True while the unranked onboarding preset applies (spec §7.1). */
  get trainingActive(): boolean {
    return !this.data.cleared && this.data.runs < 3;
  }

  // ---- traceprints ----

  /** Returns null if archive is full (caller asks the player to remove one). */
  savePrint(p: Omit<TraceprintRec, "id" | "runNumber">): TraceprintRec | null {
    const d = this.data;
    if (d.prints.length >= MAX_PRINTS) return null;
    d.printCounter++;
    const rec: TraceprintRec = { ...p, id: `p${Date.now()}_${d.printCounter}`, runNumber: d.printCounter };
    d.prints.unshift(rec);
    const fresh: string[] = [];
    if (d.prints.length >= 10 && !d.achievements.includes("archivist")) {
      d.achievements.push("archivist");
      fresh.push("archivist");
    }
    this.save();
    return rec;
  }

  deletePrint(id: string): void {
    this.data.prints = this.data.prints.filter((p) => p.id !== id);
    this.save();
  }

  toggleFav(id: string): void {
    const p = this.data.prints.find((x) => x.id === id);
    if (p) {
      p.fav = !p.fav;
      this.save();
    }
  }

  achievementRewardName(achId: string): string | null {
    const r = achievementReward(achId);
    return r ? r.item.name : null;
  }
}
